import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { tokenizeWithPositions } from '../../utils/wordLevelHighlighter';

interface HighlightedMarkdownRendererProps {
  content: string;
  wordHighlights?: Map<string, { color: string; matchCount: number }>; // word (lowercase) -> color info
  className?: string;
  isSynthesis?: boolean; // true = use background colors, false = use text colors (gray)
}

/**
 * Renders markdown with word-level highlighting applied to text nodes
 * Preserves all markdown structure (lists, headings, links, etc.)
 */
export const HighlightedMarkdownRenderer: React.FC<HighlightedMarkdownRendererProps> = ({
  content,
  wordHighlights,
  className = '',
  isSynthesis = true,
}) => {
  /**
   * Apply highlighting to a text string while preserving word boundaries
   */
  const highlightText = (text: string): React.ReactNode => {
    if (!wordHighlights || wordHighlights.size === 0) {
      return text;
    }

    const tokens = tokenizeWithPositions(text);
    
    return (
      <>
        {tokens.map((token, index) => {
          if (!token.isWord) {
            // Non-word tokens (spaces, punctuation) - render as-is
            return <React.Fragment key={index}>{token.word}</React.Fragment>;
          }

          // Check if this word should be highlighted
          const normalizedWord = token.word.toLowerCase();
          let highlight = wordHighlights.get(normalizedWord);
          
          // If no exact match, try apostrophe-stripped variant
          if (!highlight) {
            const strippedWord = normalizedWord.replace(/'/g, '');
            if (strippedWord !== normalizedWord) {
              highlight = wordHighlights.get(strippedWord);
            }
          }

          if (highlight) {
            // Synthesis: colored background, black text
            // Candidate: gray text color (de-emphasize matches)
            return (
              <span
                key={index}
                style={
                  isSynthesis
                    ? {
                        backgroundColor: highlight.color,
                        color: '#000',
                        padding: '2px 0',
                        borderRadius: '2px',
                        fontWeight: highlight.matchCount > 1 ? 'bold' : 'normal',
                      }
                    : {
                        color: highlight.color, // Gray for candidates
                        fontWeight: 'normal',
                      }
                }
                title={
                  isSynthesis && highlight.matchCount > 1
                    ? `Matched by ${highlight.matchCount} models`
                    : undefined
                }
              >
                {token.word}
              </span>
            );
          }

          // No highlight - render as plain text
          return <React.Fragment key={index}>{token.word}</React.Fragment>;
        })}
      </>
    );
  };

  const components: Components = {
    // Customize heading styles with highlighting
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold mb-4 mt-6">
        {typeof children === 'string' ? highlightText(children) : children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-bold mb-3 mt-5">
        {typeof children === 'string' ? highlightText(children) : children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-bold mb-2 mt-4">
        {typeof children === 'string' ? highlightText(children) : children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-base font-bold mb-2 mt-3">
        {typeof children === 'string' ? highlightText(children) : children}
      </h4>
    ),
    
    // Customize paragraph with highlighting
    p: ({ children }) => (
      <p className="mb-3 leading-relaxed">
        {typeof children === 'string' ? highlightText(children) : children}
      </p>
    ),
    
    // Customize links (don't highlight the URL itself)
    a: ({ children, href }) => (
      <a 
        href={href}
        className="text-blue-600 dark:text-blue-400 hover:underline" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {typeof children === 'string' ? highlightText(children) : children}
      </a>
    ),
    
    // Customize lists (preserve structure, highlight text)
    ul: ({ children }) => <ul className="list-disc list-inside mb-3 ml-4">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside mb-3 ml-4">{children}</ol>,
    li: ({ children }) => (
      <li className="mb-1">
        {typeof children === 'string' ? highlightText(children) : children}
      </li>
    ),
    
    // Customize code blocks (don't highlight code)
    code: ({ children, className: codeClassName }) => {
      const isInline = !codeClassName;
      return isInline ? (
        <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
      ) : (
        <code className="block bg-muted p-3 rounded text-sm font-mono overflow-x-auto mb-3">{children}</code>
      );
    },
    
    // Customize pre blocks (don't highlight code)
    pre: ({ children }) => <pre className="bg-muted p-3 rounded overflow-x-auto mb-3">{children}</pre>,
    
    // Customize blockquotes with highlighting
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-3">
        {children}
      </blockquote>
    ),
    
    // Customize strong/bold (apply highlighting, keep bold)
    strong: ({ children }) => (
      <strong className="font-bold">
        {typeof children === 'string' ? highlightText(children) : children}
      </strong>
    ),
    
    // Customize emphasis/italic (apply highlighting, keep italic)
    em: ({ children }) => (
      <em className="italic">
        {typeof children === 'string' ? highlightText(children) : children}
      </em>
    ),
    
    // Customize horizontal rule
    hr: () => <hr className="my-4 border-border" />,
    
    // Customize table elements with highlighting
    table: ({ children }) => (
      <table className="border-collapse border border-border my-4">
        {children}
      </table>
    ),
    thead: ({ children }) => (
      <thead className="bg-muted">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody>
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="border border-border">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="border border-border px-4 py-2 text-left font-bold">
        {typeof children === 'string' ? highlightText(children) : children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-border px-4 py-2">
        {typeof children === 'string' ? highlightText(children) : children}
      </td>
    ),
    
    // Text nodes - this is where most highlighting happens
    text: ({ children }) => {
      return <>{typeof children === 'string' ? highlightText(children) : children}</>;
    },
  };

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

